import {
  Briefcase,
  DollarSign,
  FileText,
  Landmark,
  Loader2,
  Upload,
} from "lucide-react";
import { formatDateMMDDYY } from "../../../utils/applicationFormatters";
import { formatAmount } from "@/lib/utils/formatters";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { BankLinkingLogsModal } from "../../modals/BankLinkingLogsModal";
import styles from "../../../styles/applicationDetail.module.css";
import {
  INCOME_PERIODS,
  CHECK_TYPES,
} from "../../../constants";
import {
  angularJobIncomeYearly,
  buildApplicantDisplayName,
  calculateJobDuration,
  formatIncomeAttachmentUploadMeta,
  formatIncomeDocumentationViewMeta,
  getNinjaIncomeProofViewFiles,
  getProofOfIncomeViewFiles,
  showIncomeDocumentationSection,
} from "./incomeEmploymentHelpers";
import { EmploymentView } from "./EmploymentView";
import { VerifiedIncomeSection } from "../VerifiedIncomeSection";
import { useBankLinkingLogs } from "./hooks/useBankLinkingLogs";
import { useResubmitIncomeReport } from "./hooks/useResubmitIncomeReport";
import { useResetBankLink } from "./hooks/useResetBankLink";
import type { IncomeEmploymentReadOnlyViewProps } from "../../../types/incomeEmployment";
import type {
  EmploymentJobApiRow,
  IncomeAttachmentFile,
  OtherIncomeApiRow,
} from "../../../types/incomeEmploymentApi";

export function IncomeEmploymentReadOnlyView({
  applicantId,
  applicationData,
  applicationAllData,
  onRefresh,
  currentJobs,
  previousJobs,
  otherIncomeData,
  incomeVerificationDocuments,
  handleApplicationIncomeFilePreview,
  verifiedIncome,
  bankLinking,
}: IncomeEmploymentReadOnlyViewProps) {
  const {
    canEditVerifiedIncome,
    underwriterVerifiedIncome,
    isEditingVerifiedIncome,
    setIsEditingVerifiedIncome,
    verifiedIncomeValue,
    setVerifiedIncomeValue,
    savingVerifiedIncome,
    handleSaveVerifiedIncome,
  } = verifiedIncome;

  const { ninjaAccounts, refreshAccounts } = bankLinking;

  const bankLinkingLogs = useBankLinkingLogs({
    applicantId,
    hasBankLinkingSteps: Boolean(applicationAllData?.has_bank_linking_steps),
  });

  const resubmitIncome = useResubmitIncomeReport({
    applicantId,
    applicationStatus: applicationAllData?.application_status,
    hasBankLinkingSteps: Boolean(applicationAllData?.has_bank_linking_steps),
    onRefresh,
    refreshAccounts,
  });

  const resetBankLink = useResetBankLink({
    applicantId,
    applicationStatus: applicationAllData?.application_status,
    hasBankLinkingSteps: Boolean(applicationAllData?.has_bank_linking_steps),
    onRefresh,
    refreshAccounts,
  });

  const applicantName = buildApplicantDisplayName(applicationData);
  const ninjaIncomeDocuments = getNinjaIncomeProofViewFiles(applicationData);
  const proofIncomeDocuments = getProofOfIncomeViewFiles(applicationData);

  return (
    <div
      className={`${styles.tabContent} ${styles.incomeEmploymentPremium}`}
      data-testid="application-income-tab-view-page"
    >
      <div className={styles.incomeEmploymentVerifiedIncomeRow}>
        <VerifiedIncomeSection
          isEditingVerifiedIncome={isEditingVerifiedIncome}
          verifiedIncomeValue={verifiedIncomeValue}
          savingVerifiedIncome={savingVerifiedIncome}
          underwriterVerifiedIncome={underwriterVerifiedIncome}
          canEditVerifiedIncome={canEditVerifiedIncome}
          onVerifiedIncomeValueChange={setVerifiedIncomeValue}
          onSaveVerifiedIncome={handleSaveVerifiedIncome}
          onStartEdit={() => {
            setVerifiedIncomeValue(underwriterVerifiedIncome?.toString() || "");
            setIsEditingVerifiedIncome(true);
          }}
        />
      </div>

      {/* Current Employer(s) */}
      <div className={styles.section} data-testid="application-income-tab-view-section-current-employer">
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Briefcase className="h-4 w-4" />
            </span>
            <span className={styles.sectionTitleText}>Current Employer</span>
          </h2>
        </div>
        {currentJobs.length > 0
          ? currentJobs.map((job: EmploymentJobApiRow, index: number) => (
              <div
                key={index}
                className={
                  currentJobs.length > 1
                    ? styles.incomeEmploymentViewCard
                    : undefined
                }
                style={{
                  marginBottom: index < currentJobs.length - 1 ? "2rem" : "0",
                }}
              >
                <EmploymentView
                  employmentData={job}
                  employmentType="current_job"
                  applicationData={applicationData as Record<string, unknown>}
                  applicationAllData={applicationAllData as Record<string, unknown>}
                  onPreviewFile={handleApplicationIncomeFilePreview}
                />
              </div>
            ))
          : null}
        {currentJobs.length > 0 && (
          <div className={styles.infoRow} style={{ marginTop: "12px" }}>
            <div className={styles.infoColumn}>
              <p className="m-0 text-[14px] font-medium text-[rgb(37,60,92)]">
                Total Income :{" "}
                {formatAmount(
                  currentJobs.reduce(
                    (sum: number, job: EmploymentJobApiRow) =>
                      sum +
                      angularJobIncomeYearly(
                        job.income ?? job.monthly_income,
                        job.income_period || "Per_Month",
                      ),
                    0,
                  ),
                )}{" "}
                / Per Year
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Previous Employer(s) */}
      <div className={styles.section} data-testid="application-income-tab-view-section-previous-employer">
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <Briefcase className="h-4 w-4" />
          </span>
          Previous Employer
        </h2>
        {previousJobs.length > 0
          ? previousJobs.map((job: EmploymentJobApiRow, index: number) => (
              <div
                key={index}
                className={
                  previousJobs.length > 1
                    ? styles.incomeEmploymentViewCard
                    : undefined
                }
                style={{
                  marginBottom: index < previousJobs.length - 1 ? "2rem" : "0",
                }}
              >
                <EmploymentView
                  employmentData={job}
                  employmentType="previous_job"
                  onPreviewFile={handleApplicationIncomeFilePreview}
                />
              </div>
            ))
          : null}
        {previousJobs.length > 0 && (
          <div className={styles.infoRow} style={{ marginTop: "12px" }}>
            <div className={styles.infoColumn}>
              <p className="m-0 text-[14px] font-medium text-[rgb(37,60,92)]">
                Total Income :{" "}
                {formatAmount(
                  previousJobs.reduce(
                    (sum: number, job: EmploymentJobApiRow) =>
                      sum +
                      angularJobIncomeYearly(
                        job.income ?? job.monthly_income,
                        job.income_period || "Per_Month",
                      ),
                    0,
                  ),
                )}{" "}
                / Per Year
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Other Income Source — Angular shows this section regardless of data presence */}
      <div className={styles.section} data-testid="application-income-tab-section-other-income-source">
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <DollarSign className="h-4 w-4" />
          </span>
          Other Income Source
        </h2>
        {otherIncomeData.length > 0 && (
          <>{otherIncomeData.map((income: OtherIncomeApiRow, index: number) => (
            <div
              key={index}
              className={styles.incomeEmploymentViewCard}
              style={{
                marginBottom: index < otherIncomeData.length - 1 ? "2rem" : "0",
              }}
            >
              <div className={styles.infoRow}>
                <div className={styles.infoColumn}>
                  <h6>Income Source</h6>
                  <p>
                    {income.source_name ||
                      income.income_source ||
                      income.type ||
                      "N/A"}
                  </p>
                </div>
                <div className={styles.infoColumn}>
                  <h6>Amount</h6>
                  <p>
                    {income.amount || income.income
                      ? formatAmount(income.amount || income.income)
                      : ""}{" "}
                    (
                    {income.period || income.income_period
                      ? INCOME_PERIODS[String(income.period || income.income_period)] ||
                      String(income.period || income.income_period)
                      : ""}
                    )
                  </p>
                </div>
                {!!(income.employer_name || income.company_name) && (
                  <div className={styles.infoColumn}>
                    <h6>Employer/Source Name</h6>
                    <p>{String(income.employer_name || income.company_name || "")}</p>
                  </div>
                )}
              </div>
              <div className={styles.infoRow}>
                {(income.job_start_date || income.start_date) && (
                  <div className={styles.infoColumn}>
                    <h6>Start Date</h6>
                    <p>
                      {formatDateMMDDYY(income.job_start_date || income.start_date)}
                    </p>
                  </div>
                )}
                {(income.job_end_date || income.end_date) && (
                  <div className={styles.infoColumn}>
                    <h6>End Date</h6>
                    <p>{formatDateMMDDYY(income.job_end_date || income.end_date)}</p>
                  </div>
                )}
                {(income.job_start_date || income.start_date) && (
                  <div className={styles.infoColumn}>
                    <h6>Duration (months)</h6>
                    <p>
                      {calculateJobDuration(
                        String(income.job_start_date || income.start_date || ""),
                        income.job_end_date ?? income.end_date ?? undefined,
                      )}
                    </p>
                  </div>
                )}
                {income.present_job !== undefined && (
                  <div className={styles.infoColumn}>
                    <h6>Present Job</h6>
                    <p>
                      {income.present_job === "1" || income.present_job === true
                        ? "Yes"
                        : "No"}
                    </p>
                  </div>
                )}
              </div>
              {/* Proof of Income Attachments */}
              {income.other_proof_of_income &&
                income.other_proof_of_income.length > 0 && (
                  <div className={styles.infoRow}>
                    <div className={styles.infoColumn}>
                      <h6>Proof of Income</h6>
                      <div className={styles.filesList}>
                        {income.other_proof_of_income.map(
                          (file: IncomeAttachmentFile, fileIndex: number) => (
                            <div key={fileIndex} className={styles.fileItem}>
                              <button
                                type="button"
                                className="border-0 bg-transparent p-0 text-left text-inherit hover:underline"
                                onClick={() => {
                                  void handleApplicationIncomeFilePreview(
                                    file,
                                    income.other_proof_of_income || [],
                                  );
                                }}
                              >
                                {file.filename}
                                {file.type
                                  ? ` - ${CHECK_TYPES[file.type] || file.type}`
                                  : ""}
                              </button>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          ))}</>
        )}
      </div>

      {/* Bank Linking — Angular application-details: Ninja block only if `ninjaAccounts?.length`.
          "Bank not linked" lives under Supporting Uploads. View Logs when role + has_bank_linking_steps. */}
      <div className={styles.section} data-testid="application-income-tab-view-section-bank-linking">
        <div className={`${styles.sectionHeader} ${styles.bankLinkingSectionHeader}`}>
          <h2 className={styles.sectionTitle}>
            <span className={styles.sectionTitleIcon} aria-hidden="true">
              <Landmark className="h-4 w-4" />
            </span>
            <span className={styles.sectionTitleText}>Bank Linking</span>
          </h2>
          {resubmitIncome.showResubmitIncomeReport ||
          resetBankLink.showResetBankLink ||
          bankLinkingLogs.showViewLogsButton ? (
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
              {resubmitIncome.showResubmitIncomeReport ? (
                <button
                  type="button"
                  data-testid="application-income-tab-resubmit-income-report-button"
                  onClick={resubmitIncome.openConfirm}
                  disabled={resubmitIncome.processing}
                  aria-busy={resubmitIncome.processing}
                  className="inline-flex items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {resubmitIncome.processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  Retry Income Report
                </button>
              ) : null}
              {resetBankLink.showResetBankLink ? (
                <button
                  type="button"
                  data-testid="application-income-tab-reset-bank-link-button"
                  onClick={resetBankLink.openConfirm}
                  disabled={resetBankLink.processing}
                  aria-busy={resetBankLink.processing}
                  className="inline-flex items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {resetBankLink.processing ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  Reset Bank Link
                </button>
              ) : null}
              {bankLinkingLogs.showViewLogsButton ? (
                <button
                  type="button"
                  data-testid="application-income-tab-view-bank-linking-logs-button"
                  onClick={() => void bankLinkingLogs.openBankLinkingLogs()}
                  disabled={!applicantId || bankLinkingLogs.logsLoading}
                  aria-busy={bankLinkingLogs.logsLoading}
                  className="inline-flex items-center gap-2 rounded-md bg-[#1597ff] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#0d7acc] disabled:cursor-not-allowed disabled:bg-slate-400"
                >
                  {bankLinkingLogs.logsLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  ) : null}
                  View Logs
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        {ninjaAccounts.length > 0 && (
          <>{ninjaAccounts.map((ninjaAccount, attachIndex) => {
            const status = ninjaAccount.ibv_report_status || "";
            const showBankDetail = status !== "initiated";
            const showFetchUi = ["initiated", "ibv_data_acquire_failed"].includes(
              status,
            );

            return (
              <div
                key={ninjaAccount.pli_id ?? attachIndex}
                className={styles.infoRow}
                style={{ marginBottom: "1rem", alignItems: "flex-start" }}
              >
                {showBankDetail && (
                  <div className={styles.infoColumn}>
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 text-slate-700">
                        <Landmark className="h-8 w-8" aria-hidden />
                      </div>
                      <p className="m-0">
                        <strong>{ninjaAccount.bank_name}</strong>
                        <br />
                        {(ninjaAccount as { account_name?: string }).account_name ||
                          ""}{" "}
                        {ninjaAccount.account_type}
                      </p>
                    </div>
                  </div>
                )}
                {showFetchUi && (
                  <div className={styles.infoColumn}>
                    <h6>Processing Result</h6>
                  </div>
                )}
              </div>
            );
          })}</>
        )}
      </div>

      {/* Supporting Uploads — Angular application-details #employmentDataView always renders section 5 */}
      <div className={styles.section} data-testid="application-income-tab-section-supporting-uploads">
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <Upload className="h-4 w-4" />
          </span>
          Supporting Uploads
        </h2>
        {ninjaAccounts.length === 0 && applicationData?.bank_name ? (
          <div className="ml-1 mb-3">
            <p className="m-0 text-[15px] text-slate-800">
              <strong>Bank not linked</strong>
              {" - "}
              {applicationData.bank_name}
            </p>
          </div>
        ) : null}
        {ninjaIncomeDocuments.length > 0 && (
          <div className="mt-4 flex flex-col gap-3">
            {ninjaIncomeDocuments.map((file: IncomeAttachmentFile, index: number) => {
              const { displayName, uploadedMetaText } = formatIncomeAttachmentUploadMeta(
                file,
                applicantName,
              );

              return (
                <div
                  key={file.attachment_id || index}
                  className="flex items-center gap-2 text-[14px] text-slate-800"
                >
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <button
                    type="button"
                    className="break-all border-0 bg-transparent p-0 text-left text-inherit hover:underline"
                    onClick={() => {
                      void handleApplicationIncomeFilePreview(
                        file,
                        incomeVerificationDocuments,
                      );
                    }}
                  >
                    {displayName} {uploadedMetaText ? `- ${uploadedMetaText}` : ""}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showIncomeDocumentationSection(applicationData) && (
      <div className={styles.section} data-testid="application-income-tab-section-income-documentation">
        <h2 className={styles.sectionTitle}>
          <span className={styles.sectionTitleIcon} aria-hidden="true">
            <FileText className="h-4 w-4" />
          </span>
          Income Documentation
        </h2>
        {proofIncomeDocuments.length > 0 && (
          <div className="mt-4 flex flex-col gap-3">
            {proofIncomeDocuments.map((file: IncomeAttachmentFile, index: number) => {
              const { displayName, uploadedMetaText } = formatIncomeDocumentationViewMeta(file);

              return (
                <div
                  key={file.attachment_id || index}
                  className="flex items-center gap-2 text-[14px] text-slate-800"
                >
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <button
                    type="button"
                    className="break-all border-0 bg-transparent p-0 text-left text-inherit hover:underline"
                    onClick={() => {
                      void handleApplicationIncomeFilePreview(
                        file,
                        incomeVerificationDocuments,
                      );
                    }}
                  >
                    {displayName} {uploadedMetaText ? `- ${uploadedMetaText}` : ""}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
      )}

      <ConfirmDialog
        isOpen={bankLinkingLogs.emptyLogsDialogOpen}
        title="Bank linking logs"
        message="No bank linking steps were found for this applicant."
        primaryLabel="OK"
        secondaryLabel=""
        onPrimary={() => bankLinkingLogs.setEmptyLogsDialogOpen(false)}
        onSecondary={() => bankLinkingLogs.setEmptyLogsDialogOpen(false)}
        onDismiss={() => bankLinkingLogs.setEmptyLogsDialogOpen(false)}
        overlayDataTestId="application-income-tab-bank-linking-logs-empty"
        primaryButtonDataTestId="application-income-tab-bank-linking-logs-empty-ok-button"
        closeButtonDataTestId="application-income-tab-bank-linking-logs-empty-close-button"
      />

      <ConfirmDialog
        isOpen={resubmitIncome.confirmOpen}
        overlayDataTestId="application-income-tab-resubmit-income-confirm-overlay"
        primaryButtonDataTestId="application-income-tab-resubmit-income-confirm-primary-button"
        secondaryButtonDataTestId="application-income-tab-resubmit-income-confirm-secondary-button"
        closeButtonDataTestId="application-income-tab-resubmit-income-confirm-close-button"
        title="Retry Income Report"
        message="Do you want to Retry the Income Report?"
        primaryLabel="Retry"
        onPrimary={() => void resubmitIncome.performResubmit()}
        secondaryLabel="Cancel"
        onSecondary={resubmitIncome.closeConfirm}
        presentation="saveChanges"
        primaryFirst
      />

      <ConfirmDialog
        isOpen={resetBankLink.confirmOpen}
        overlayDataTestId="application-income-tab-reset-bank-link-confirm-overlay"
        primaryButtonDataTestId="application-income-tab-reset-bank-link-confirm-primary-button"
        secondaryButtonDataTestId="application-income-tab-reset-bank-link-confirm-secondary-button"
        closeButtonDataTestId="application-income-tab-reset-bank-link-confirm-close-button"
        title="Reset Bank Link"
        message="Do you want to reset the bank link? This will mark the bank linking step as failed so the applicant can try again."
        primaryLabel="Confirm"
        onPrimary={() => void resetBankLink.performReset()}
        secondaryLabel="Cancel"
        onSecondary={resetBankLink.closeConfirm}
        onDismiss={resetBankLink.closeConfirm}
        primaryFirst
        actionStyle="application"
        primaryLoading={resetBankLink.processing}
      />

      <BankLinkingLogsModal
        isOpen={bankLinkingLogs.logsModalOpen}
        onClose={bankLinkingLogs.closeLogsModal}
        applicantId={applicantId}
        initialRequests={bankLinkingLogs.logRequests}
      />
    </div>
  );
}
